<script lang="ts">
    import { getContext, tick } from 'svelte';
    import { writable } from 'svelte/store';
    import { addDays, addMonths, format } from 'date-fns';
    import { signed } from '@/PlanReport/format';
    import { slide } from 'svelte/transition';
    import { LocalDate } from "@/time";
    import { initForecastInputs } from "@/Util/api";
    import { getCurrentFitnessSignature, getCurrentTrainingLoads, round, type XSSDetails } from '@/Util/utils';
    import { ProgramType, SpecificityTypes, defaultGoalPlan, FocusTypes, unlimitedWeeklyHours } from "@/Common/constants";
    import type { CalendarDefaults, ProgramFocusTypes, XfaiFormProps } from "@/Common/constants";
    import { SelectSlider, DatePicker } from "@/Common";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";
    import { FrontierLabel } from "./ProgramElements";
    import FrontierEnds from "./ProgramElements/SetupFormInputs/FrontierEnds.svelte";
    import { createProgramAchievability } from './modules/ProgramAchievability';
    import { assessGoal, createForecastInputs, estimateTargets, type GoalAssessment, type GoalForecastTargets, type GoalStatus } from './modules/ForecastFunctions';
    import { createUserFocusPowers } from './modules/UserFocusPowers';
    import type { MedianAndWeatherStores, MyFitnessStores } from '@/MyFitness/stores';
    import { cn } from '@/lib/utils';
    import WeekAvailability from '@/SetupWizards/WeekAvailability.svelte';
    import SignatureAdjustDialog from '@/SetupWizards/SignatureAdjustDialog.svelte';
    import FrontierAdjustDialog from '@/SetupWizards/FrontierAdjustDialog.svelte';
    import { getRequest } from '@/Util';

    /*
     * The Goal plan form for the account setup wizard: shows the user's fitness signature and lets them pick
     * Threshold Focus (Sprint Time-Triallist, 20 minute power), VO2Max Focus (Rouleur, 6 minute power) or Anaerobic Focus
     * (Road Sprinter, 2 minute power). The goal is a power increase by a target date, with Mixed specificity.
     * Mixed keeps high intensity in the plans: with Polar, and with GC Specialist (8 minute power) as the middle choice, the
     * buildable plans of riders with little time let their high and peak training loads fall.
     * Trial users get a preset date and increase that can't be changed.
     */

    const myFitness = getContext<MyFitnessStores>('myFitness')
    const { trainingAdvice, userParams } = myFitness
    const { trainingMedians } = getContext<MedianAndWeatherStores>('medianAndWeather')

    type $$Props = XfaiFormProps<ProgramType.Goal> & { trial: boolean }
    export let plan = writable({...defaultGoalPlan})
    export let forecastInputs: any
    export let disabled: boolean
    export let trial: boolean
    export let achievabilityId = "goal-achievability"
    export let forecastReady = false
    export const refresh = updateInitialInputs
    let className = ""
    export {className as class}

    // minutes: the focus's duration, whose power the plan improves (20, 6 and 2 minute power)
    const choices: {label: string, focus: ProgramFocusTypes, icon: string, description: string, minutes: number}[] = [
        {label: "Threshold Focus", focus: FocusTypes.SprintTimeTriallist, icon: "fa-mountain", description: "This plan focuses entirely on threshold and 20 minute power.", minutes: 20},
        {label: "VO2Max Focus", focus: FocusTypes.Rouleur, icon: "fa-bicycle", description: "This plan focuses on improving your VO2max and 6 minute power.", minutes: 6},
        {label: "Anaerobic Focus", focus: FocusTypes.RoadSprinter, icon: "fa-bolt", description: "This plan focuses on your top end and 2 minute power.", minutes: 2},
    ]
    // Each choice's power today, from the minute powers the plan's focus power comes from (createUserFocusPowers), so the
    // cards show the same numbers as the forecast's results.
    $: powerToday = (c: typeof choices[number]): number | null =>
        $trainingAdvice?.current_minute_powers?.find((mp: { x: number, userp: number }) => mp.x == c.minutes*60)?.userp ?? null

    // Today's, which change when the user adjusts their signature (adjustSignature)
    let currentSignature: XSSDetails
    let currentTL: XSSDetails
    $: currentSignature = getCurrentFitnessSignature($trainingAdvice)
    $: currentTL = getCurrentTrainingLoads($trainingAdvice)

    /* Presets: 3 months out, and the improvement most riders can reach with the availability the wizard suggests */
    const MONTHS = 3
    const MAX_IMPROVEMENT = 20
    let targetDate = LocalDate.format(addMonths(new Date(), MONTHS))
    // Found by forecasting 12 riders (functional weight 55 to 90 kg, 4 to 8 hours a week) for each focus, over 3 months with
    // the suggested availability (5 flex rides a week), Polar specificity and maximum training responsiveness, and
    // raising the improvement until the plan stopped reaching it. Of the riders whose availability covers what they ride now
    // (4 and 6 hours a week), every one reached 12w as a Climber and 10w as an All-Rounder, and three quarters reached 10w as a
    // Sprinter. 10w is used for all three. Riders going above it are not likely to have enough availability. (That calibration
    // was done with Climber and Rouleur and Polar, before the choices became 20, 6 and 2 minute power with Mixed.)
    // It is only used until the estimated frontier is known, or when it can't be.
    const DEFAULT_IMPROVEMENT = 10
    const defaultImprovements: Record<string, number> = {
        [FocusTypes.SprintTimeTriallist]: DEFAULT_IMPROVEMENT,
        [FocusTypes.Rouleur]: DEFAULT_IMPROVEMENT,
        [FocusTypes.RoadSprinter]: DEFAULT_IMPROVEMENT,
    }
    let choice = choices[1]
    let improvement = defaultImprovements[choice.focus]

    // The goal against the plan's training capacity (assessGoal): the estimated frontier, in watts, and the bands of the
    // warning. The frontier depends on the date and the focus, the earliest date past it also on the improvement. The slider
    // reaches a little past the frontier (Rigid: a plan that needs every workout), or 20w without one.
    const MIN_IMPROVEMENT = 0
    let assessment: GoalAssessment | null = null
    let assessmentKey = ""
    $: frontier = assessment ? assessment.frontierFocusPower - assessment.currentFocusPower : null
    // The bands in the slider's whole watts: fine up to 90% of the frontier, approaching up to the frontier as shown, beyond
    // past it (see assessCapacity in optimizer.cpp).
    $: frontierWatts = frontier != null ? Math.round(frontier) : null
    // Trial users keep the fixed range their preset improvement sits in.
    $: showFrontier = frontierWatts != null && !trial
    // A watt past the frontier, in red: plans level off at about the frontier (calculateEvent caps a goal's loads there),
    // so a goal a watt past it can still be met, one further past rarely is. On xert6's own inputs (20, 6, 4 and 2 minute
    // focuses, 4 to 13 weeks), met within 1 W: 7/12 at the frontier, 3/12 at 110%, 1/12 at 130%; the plans levelled off
    // between 1.4 W short of the frontier and 1.2 W past it.
    const BEYOND_WATTS = 1
    $: sliderMax = showFrontier ? Math.max(MIN_IMPROVEMENT + 1, frontierWatts! + BEYOND_WATTS) : MAX_IMPROVEMENT
    // The share of the frontier that is still fine (fineShare in assessCapacity)
    const FINE_SHARE = 0.9
    const fineWattsOf = (frontier: number) => Math.max(MIN_IMPROVEMENT, Math.floor(FINE_SHARE*frontier))
    $: fineWatts = frontier != null ? fineWattsOf(frontier) : null
    // When the step opens, and when the focus changes, the improvement starts at the most without a warning (fineWatts). Trial users
    // keep their preset improvement.
    let improvementFromFrontier = !trial
    $: if (improvement > sliderMax) {
        improvement = sliderMax
        onSettingsInput()
    }
    // Without an assessment, above the improvement most riders reach is approaching.
    let goalStatus: GoalStatus
    // The week's availability the forecast runs with: a change to it is assessed again shortly after (see the availability
    // row), and an assessment made with other availability is stale like one for another date or focus.
    let availabilityJson = JSON.stringify($plan.cal_settings.defaults)
    let availabilityTimer: ReturnType<typeof setTimeout>
    $: {
        const json = JSON.stringify($plan.cal_settings.defaults)
        if (json !== availabilityJson) {
            availabilityJson = json
            clearTimeout(availabilityTimer)
            availabilityTimer = setTimeout(onSettingsInput, 400)
        }
    }
    // An assessment for another date, focus or availability (until the new one arrives) says nothing about this goal, so no
    // warning then.
    let assessmentFor = ""
    $: goalStatus = assessment && frontierWatts != null && fineWatts != null
        ? (assessmentFor !== `${targetDate} ${$plan.targetAthlete} ${availabilityJson}` ? "fine"
            : improvement <= fineWatts ? "fine" : improvement <= frontierWatts ? "approaching" : "beyond")
        : (improvement > defaultImprovements[choice.focus] ? "approaching" : "fine")
    // The earliest date is for the improvement it was assessed with, shown only while that is the one on the slider.
    $: earliestDate = assessment && assessmentKey === `${targetDate} ${$plan.targetAthlete} ${$plan.focusPower} ${availabilityJson}` ? assessment.earliestDate : null

    let focusPower = createUserFocusPowers(trainingAdvice)
    const {currentFocusPower} = focusPower

    function updatePlan() {
        focusPower.updateFocusPower($plan.targetAthlete)
        $plan.focusPower = Math.round($currentFocusPower + improvement)
        $plan.targetDate = LocalDate.atNoon(targetDate)
    }

    $plan.startDate = null
    $plan.specificity = SpecificityTypes.Mixed
    $plan.targetsMode = "focus"
    $plan.targetAthlete = choice.focus
    // The plan is forecast with unlimited weekly hours, so its days are limited only by the availability. The plan is then
    // saved with the highest week the forecast came to as its Max Weekly Hours (see FirstTimePlanStep).
    $plan.settings.maxWeeklyHours = unlimitedWeeklyHours
    updatePlan()

    /* Achievability */
    export const programAchievability = createProgramAchievability(ProgramType.Goal)
    const {overallAchievability, hoursAchievability, statusMessage, targetAchievability} = programAchievability
    let targetSignature: XSSDetails
    let targetTL: XSSDetails
    let initialInputs = null

    function validateTargets() {
        const difficulty = !$plan.settings.difficultyFromTrainingLoad ? $plan.settings.difficulty : null
        return programAchievability.validateTargets($plan.targetDate, new Date(initialInputs.t0*1000), targetTL, currentTL, $plan.settings.maxWeeklyHours, difficulty)
    }

    function getForecastInputs() {
        return createForecastInputs<GoalForecastTargets>(
            {
                program: ProgramType.Goal,
                focus_power: $plan.focusPower,
                focus: $plan.targetAthlete,
                specificity_rating: $plan.specificity,
                start: $plan.targetDate,
                currentFocusPower: $currentFocusPower,
                manualSignature: null,
            },
            $userParams.athletes,
            initialInputs,
            $plan.settings
        )
    }

    // Each call supersedes the ones before it: a slower earlier call must not apply its inputs (an older target date) last.
    let inputsRequest = 0

    async function updateInitialInputs() {
        const request = ++inputsRequest
        forecastReady = false
        forecastInputs = null
        await tick() // await to ensure DOM updates
        const inputs = await initForecastInputs({
            start: $plan.targetDate,
            startDate: $plan.startDate,
            focus_power: $plan.focusPower,
            focus: $plan.targetAthlete,
            specificity_rating: $plan.specificity,
            mode: "target_focus",
        })
        if (request !== inputsRequest) return
        initialInputs = inputs
        const newForecastInputs = getForecastInputs()

        const key = `${targetDate} ${$plan.targetAthlete} ${$plan.focusPower} ${availabilityJson}`
        if (key !== assessmentKey) {
            const result = await assessGoal(newForecastInputs, $plan.cal_settings, $trainingMedians, "wizard-goal")
            if (request !== inputsRequest) return
            assessment = result
            assessmentKey = key
            assessmentFor = `${targetDate} ${$plan.targetAthlete} ${availabilityJson}`
            if (improvementFromFrontier && result) {
                improvementFromFrontier = false
                const watts = fineWattsOf(result.frontierFocusPower - result.currentFocusPower)
                if (watts !== improvement) {
                    improvement = watts
                    onSettingsInput()
                    return
                }
            }
        }

        const targets = await estimateTargets(newForecastInputs, $plan.cal_settings, $trainingMedians)
        if (request !== inputsRequest) return
        forecastInputs = newForecastInputs
        targetTL = targets.targetTL
        targetSignature = targets.targetSignature
        validateTargets()
        forecastReady = true
    }
    updateInitialInputs()

    function onChoice(c: typeof choices[number]) {
        if (disabled) return
        choice = c
        // The improvement moves to the new focus's default once its frontier is known (see improvementFromFrontier)
        if (trial) improvement = defaultImprovements[c.focus]
        improvementFromFrontier = !trial
        $plan.targetAthlete = c.focus
        updatePlan()
        updateInitialInputs()
    }

    function onSettingsInput() {
        updatePlan()
        updateInitialInputs()
    }

    // Adjusting the fitness signature (SignatureAdjustDialog) sets it as the user's starting signature. The focus powers,
    // frontier and targets then follow it, and the improvement starts again from the new frontier.
    let adjustOpen = false
    async function adjustSignature(ftp: number, pp: number) {
        const response = await window.fetch('/wizard/signature', { ...getRequest(), method: 'POST', body: JSON.stringify({ ftp, pp }) })
        const result = await response.json().catch(() => ({}))
        // The dialog checks the values, so a failure here is the server's, whose message means nothing to the user
        if (!response.ok || !result?.training_advice) throw new Error("Your fitness signature could not be saved. Please try again.")
        $trainingAdvice = result.training_advice
        improvementFromFrontier = !trial
        assessmentKey = ""
        onSettingsInput()
    }

    // Adjusting the frontier (FrontierAdjustDialog): the frontier a target date and availability reach, assessed as the form's
    // goal is, with the improvement at the fine share of the wanted frontier so that the earliest date the goal is fine is
    // the earliest the frontier is reached. The forecast inputs for a date are fetched once each time the dialog opens.
    let frontierOpen = false
    let frontierInputs = new Map<string, Promise<any>>()
    function openFrontier() {
        frontierInputs = new Map()
        frontierOpen = true
    }
    async function assessFrontier(date: string, defaults: CalendarDefaults, wanted: number) {
        const start = LocalDate.atNoon(date)
        const focus_power = $currentFocusPower + FINE_SHARE*wanted
        const key = `${date} ${$plan.targetAthlete} ${Math.round(focus_power)}`
        if (!frontierInputs.has(key)) {
            frontierInputs.set(key, initForecastInputs({ start, startDate: $plan.startDate, focus_power: Math.round(focus_power), focus: $plan.targetAthlete, specificity_rating: $plan.specificity, mode: "target_focus" }))
        }
        const inputs = await frontierInputs.get(key)
        const forecastInputs = createForecastInputs<GoalForecastTargets>(
            { program: ProgramType.Goal, focus_power, focus: $plan.targetAthlete, specificity_rating: $plan.specificity, start, currentFocusPower: $currentFocusPower, manualSignature: null },
            $userParams.athletes,
            structuredClone(inputs),
            $plan.settings
        )
        const result = await assessGoal(forecastInputs, { ...$plan.cal_settings, defaults }, $trainingMedians)
        return result && { frontier: result.frontierFocusPower - result.currentFocusPower, earliestDate: result.earliestDate }
    }
    function applyFrontier(date: string, defaults: CalendarDefaults) {
        $plan.cal_settings.defaults = defaults
        if (date !== targetDate) {
            targetDate = date
            onSettingsInput()
        }
    }
</script>

<SignatureAdjustDialog bind:open={adjustOpen} ftp={currentSignature.xlss} pp={currentSignature.xpss} onSave={adjustSignature}/>
{#if showFrontier}
    <FrontierAdjustDialog bind:open={frontierOpen} frontier={frontierWatts ?? 0} {targetDate} defaults={$plan.cal_settings.defaults}
        trainingMedians={$trainingMedians} assess={assessFrontier} onApply={applyFrontier}/>
{/if}

<div class={cn("flex flex-col gap-6 items-center text-center", className)}>

    <!-- Fitness signature as of today -->
    <div class="flex flex-col items-center gap-3">
        <div class="flex items-center gap-2 text-sm">
            <span class="text-slate-500 tracking-tight">Your fitness signature today</span>
            <button type="button" class="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-600 hover:border-xerteal-400 hover:text-xerteal-700 disabled:opacity-60"
                {disabled} on:click={() => adjustOpen = true}>
                <i class="fa-regular fa-pen text-[10px]"/> Adjust
            </button>
        </div>
        <div class="flex items-end gap-x-10 gap-y-4 justify-center flex-wrap">
            <div class="flex flex-col items-center leading-none gap-2">
                <span class="font-black text-5xl md:text-6xl tracking-tight text-[--color-xlss]">{round(currentSignature.xlss)}<span class="text-2xl font-medium text-slate-500 ml-1">w</span></span>
                <span class="text-slate-600 font-medium">Threshold Power (TP)</span>
            </div>
            <div class="flex gap-8">
                <div class="flex flex-col items-center leading-none gap-2">
                    <span class="font-bold text-3xl text-[--color-xhss]">{round(currentSignature.xhss/1000, 1)}<span class="text-base font-medium text-slate-500 ml-1">kJ</span></span>
                    <span class="text-slate-600 text-sm">HIE</span>
                </div>
                <div class="flex flex-col items-center leading-none gap-2">
                    <span class="font-bold text-4xl text-[--color-xpss]">{round(currentSignature.xpss)}<span class="text-base font-medium text-slate-500 ml-1">w</span></span>
                    <span class="text-slate-600 text-sm">PP</span>
                </div>
            </div>
        </div>
    </div>

    <div class="h-px w-full bg-black/10"/>

    <!-- What to improve -->
    <div class="flex flex-col items-center gap-4 w-full">
        <span class="font-bold text-xl">Improve your Power</span>
        <!-- Three across on phones too, without the descriptions, so the date and improvement stay near the top -->
        <div class="grid grid-cols-3 gap-2 sm:gap-3 w-full max-w-2xl" role="radiogroup" aria-label="Improve your power">
            {#each choices as c}
                <button type="button" role="radio" aria-checked={choice === c} {disabled} on:click={() => onChoice(c)}
                    class={cn("flex flex-col items-center gap-1 rounded-md border-2 px-2 py-2 sm:px-4 sm:py-3 transition-all disabled:opacity-60",
                        choice === c ? "border-xerteal-400 bg-xerteal-50 text-xerteal-700 shadow-sm" : "border-slate-200 hover:border-xerteal-300 text-slate-600")}>
                    <i class={cn("fa-regular text-xl sm:text-2xl", c.icon)}/>
                    <span class="font-semibold text-sm sm:text-base leading-tight">{c.label}</span>
                    <span class="hidden sm:block text-xs text-slate-500">{c.description}</span>
                    {#if powerToday(c) != null}
                        <!-- The power today and, for the choice, the power the plan aims for -->
                        <span class={cn("text-xs sm:text-sm tabular-nums whitespace-nowrap", choice === c ? "font-semibold text-xerteal-800" : "text-slate-500")}>
                            {Math.round(powerToday(c) ?? 0)}w{#if choice === c}&nbsp;→&nbsp;{Math.round($plan.focusPower)}w{/if}
                        </span>
                    {/if}
                </button>
            {/each}
        </div>
    </div>

    <!-- Target date, improvement and availability. Laid out by the form's own width: a column of label and value on phones
         (the slider and the days with rows of their own, wide enough to use with a thumb), label, control and value in
         between, and target date and improvement on one row when there is room for both (40rem: the wizard's form on a laptop). -->
    <div class="@container w-full max-w-4xl">
    <div class="grid grid-cols-[1fr,auto] @xl:grid-cols-[auto,1fr,auto] @[40rem]:grid-cols-[auto,auto,auto,1fr,auto] items-center gap-x-4 gap-y-5 w-full text-left">
        <span class="text-slate-600 @[40rem]:self-start @[40rem]:pt-2.5">Target Date</span>
        <div class="@xl:col-span-2 @[40rem]:col-span-1 @[40rem]:self-start">
            <DatePicker class="w-52 @[40rem]:w-40 text-base" disabled={trial || disabled}
                min={LocalDate.format(addDays(new Date(), 1))} max={LocalDate.format(addDays(addMonths(new Date(), 6), -1))}
                bind:value={targetDate} on:input={onSettingsInput}
            />
        </div>

        <!-- The improvement, in the slider's thumb, from Flexible (room to miss workouts) to Rigid at the frontier, with the
             last tenth before it in yellow and a little past it in red. On wider screens the label and frontier line up with
             the track, not with the track and the ends' labels below it. -->
        <span class="flex items-center gap-1 text-slate-600 @xl:self-start @xl:pt-2 @[40rem]:pl-4">
            Improvement
            {#if showFrontier && frontierWatts != null && improvement > frontierWatts}
                <span transition:slide={{axis: "x"}}>
                    <HelpTooltip icon="fa-solid fa-circle-exclamation" iconClass="text-red-600 -my-1" label="About this improvement"
                        message="This is past your Estimated Frontier. Plans level off at about the frontier, so yours may fall a watt or so short of this by the target date, though it may still reach it. Your plan won't allow you to miss any workouts."/>
                </span>
            {:else if showFrontier && fineWatts != null && improvement > fineWatts}
                <span transition:slide={{axis: "x"}}>
                    <HelpTooltip icon="fa-solid fa-triangle-exclamation" iconClass="text-amber-500 -my-1" label="About this improvement"
                        message="Your training plan may not allow you to miss any workouts without modifying your plan's configuration."/>
                </span>
            {/if}
        </span>
        <div class="col-span-2 row-start-3 -mt-4 @xl:col-span-1 @xl:row-start-auto @xl:mt-0">
            <SelectSlider min={MIN_IMPROVEMENT} max={sliderMax} step={1} thumbSize={38} filled warnFrom={showFrontier ? fineWatts : null} errorFrom={showFrontier ? frontierWatts : null}
                disabled={trial || disabled} bind:value={improvement} on:input={onSettingsInput} aria-label="Power improvement in watts">
                <span slot="thumb" class="text-xs font-bold text-slate-800 tabular-nums leading-none">+{improvement}<span class="font-medium text-slate-500">w</span></span>
            </SelectSlider>
            <!-- Not when the frontier is at the slider's start: the improvement is past it all the way -->
            {#if showFrontier && frontierWatts != null && frontierWatts > MIN_IMPROVEMENT}
                <FrontierEnds min={MIN_IMPROVEMENT} max={sliderMax} frontier={frontierWatts} thumbSize={38}/>
            {/if}
        </div>
        {#if showFrontier}
            <!-- The frontier, which the slider goes a little past, and its adjustment -->
            <div class="flex flex-col items-end col-start-2 row-start-2 @xl:self-start @xl:col-start-auto @xl:row-start-auto">
                <div class="flex items-center gap-2">
                    <span class="font-semibold tabular-nums">{signed(frontierWatts ?? 0)}<span class="text-slate-500 text-sm font-normal">w</span></span>
                    <button type="button" class="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-600 hover:border-xerteal-400 hover:text-xerteal-700 disabled:opacity-60"
                        {disabled} on:click={openFrontier}>
                        <i class="fa-regular fa-wrench text-[10px]"/> Adjust
                    </button>
                </div>
                <FrontierLabel/>
            </div>
        {:else}
            <span class="font-semibold text-right col-start-2 row-start-2 @xl:w-14 @xl:self-start @xl:pt-1.5 @xl:col-start-auto @xl:row-start-auto">+{improvement}<span class="text-slate-500 text-sm font-normal">w</span></span>
        {/if}

        <!-- The week's riding time the plan is built on, as found from the rider's history or suggested from their hours: a
             day opens its availability to change it. On phones the days get a row of their own. -->
        <span class="text-slate-600 @xl:self-start @xl:pt-3">Availability</span>
        <span class="text-xs text-slate-400 text-right @xl:hidden">Tap a day to change it</span>
        <WeekAvailability class="col-span-2 @[40rem]:col-span-4" {disabled} bind:defaults={$plan.cal_settings.defaults} trainingMedians={$trainingMedians}/>
        {#if goalStatus !== "fine" && !showFrontier}
            <div class={cn("col-span-2 @xl:col-span-3 @[40rem]:col-span-5 flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
                    goalStatus === "beyond" ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800")}
                role="alert" transition:slide>
                <i class={cn("fa-solid fa-triangle-exclamation mt-0.5", goalStatus === "beyond" ? "text-red-600" : "text-amber-600")}/>
                {#if !assessment}
                    <span>This may be more improvement than your riding time allows by the target date. A bigger goal needs more or longer rides.</span>
                {:else if goalStatus === "approaching"}
                    <span>
                        +{improvement}w is close to your Estimated Frontier of {signed(frontierWatts ?? 0)}w, so your plan may fall a few watts short of it.
                        {#if earliestDate}A target date of {format(earliestDate, "d MMM yyyy")} would give it more room.{/if}
                    </span>
                {:else}
                    <span>
                        +{improvement}w is past your Estimated Frontier of {signed(frontierWatts ?? 0)}w, so your plan is unlikely to reach it by {format($plan.targetDate, "d MMM yyyy")}.
                        {#if earliestDate}A target date of {format(earliestDate, "d MMM yyyy")} would put it within reach.{/if}
                        You can also choose a smaller improvement or add riding time to your availability.
                    </span>
                {/if}
            </div>
        {/if}
    </div>
    </div>
    {#if trial}
        <div class="flex items-center gap-2 text-sm text-slate-500 italic -mt-4">
            <i class="fa-regular fa-lock"/>
            Target Date and Improvement are preset for trial users.
        </div>
    {/if}

    <slot name="generate"/>
</div>
